import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { updateCompany } from '../services/api';
import { Company } from '../types';
import './Modal.css';

interface EditCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  company: Company | null;
}

const EditCompanyModal: React.FC<EditCompanyModalProps> = ({ isOpen, onClose, onSuccess, company }) => {
  const [formData, setFormData] = useState({
    name: '',
    sellerName: '',
    storeName: '',
    companyId: '',
    address: '',
    gstin: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        sellerName: company.sellerName || '',
        storeName: company.storeName || '',
        companyId: company.companyId || '',
        address: company.address || '',
        gstin: company.gstin || '',
        phone: company.phone || ''
      });
    }
  }, [company]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const companyId = company?._id || company?.id;
      if (!companyId) {
        throw new Error('Company ID not found');
      }
      
      await updateCompany(companyId, formData);
      alert('Company updated successfully!');
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update company');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !company) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Company</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="name">Company Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Madquick Pvt Ltd"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="sellerName">Seller Name</label>
              <input
                type="text"
                id="sellerName"
                name="sellerName"
                value={formData.sellerName}
                onChange={handleChange}
                placeholder="e.g., Amit Sharma"
              />
            </div>

            <div className="form-group">
              <label htmlFor="storeName">Store Name</label>
              <input
                type="text"
                id="storeName"
                name="storeName"
                value={formData.storeName}
                onChange={handleChange}
                placeholder="e.g., Shrinath"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="companyId">Seller ID</label>
              <input
                type="text"
                id="companyId"
                name="companyId"
                value={formData.companyId}
                onChange={handleChange}
                placeholder="e.g., 5123456"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="address">Address *</label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              rows={3}
              placeholder="e.g., 5 Civil Line Sagar, Above Khare Classes, Near Hanuman Mandir, Sagar, M.P."
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="gstin">GSTIN</label>
              <input
                type="text"
                id="gstin"
                name="gstin"
                value={formData.gstin}
                onChange={handleChange}
                placeholder="e.g., 23ABCDE1234F5Z8"
                maxLength={15}
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone Number *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="e.g., +91 9123456789"
              />
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update Company'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCompanyModal;
